import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromptHelperPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    goal: '',
    audience: '',
    tone: '',
    constraints: ''
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.goal.trim()) {
      toast.error('Please enter your goal');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/prompt-helper`, formData);
      setResult(response.data);
    } catch (error) {
      console.error('Error generating prompt:', error);
      toast.error('Failed to generate prompt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result?.improved_prompt) {
      navigator.clipboard.writeText(result.improved_prompt);
      setCopied(true);
      toast.success('Prompt copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Layout>
      <div className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-semibold text-black mb-6" data-testid="prompt-helper-title">
              Help me write a better prompt
            </h1>
            <p className="text-xl md:text-2xl leading-relaxed text-[#333333] mb-12">
              Tell us what you want to accomplish, and we'll help you create a clear, effective prompt.
            </p>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <Label htmlFor="goal" className="text-lg font-semibold text-black mb-2 block">
                  What do you want to accomplish? *
                </Label>
                <Textarea
                  id="goal"
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  placeholder="Example: Write a blog post about climate change"
                  className="min-h-[120px] bg-gray-50 border-2 border-transparent focus:border-[#0044CC] focus:bg-white rounded-xl text-lg p-4 w-full transition-all placeholder:text-gray-500 resize-none"
                  data-testid="goal-input"
                />
              </div>

              <div>
                <Label htmlFor="audience" className="text-lg font-semibold text-black mb-2 block">
                  Who is your audience?
                </Label>
                <Input
                  id="audience"
                  value={formData.audience}
                  onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                  placeholder="Example: High school students"
                  className="h-14 px-4 bg-gray-50 border-2 border-transparent focus:border-[#0044CC] focus:bg-white rounded-xl text-lg w-full transition-all placeholder:text-gray-500"
                  data-testid="audience-input"
                />
              </div>

              <div>
                <Label htmlFor="tone" className="text-lg font-semibold text-black mb-2 block">
                  What tone should it have?
                </Label>
                <Input
                  id="tone"
                  value={formData.tone}
                  onChange={(e) => setFormData({ ...formData, tone: e.target.value })}
                  placeholder="Example: Professional, friendly, casual"
                  className="h-14 px-4 bg-gray-50 border-2 border-transparent focus:border-[#0044CC] focus:bg-white rounded-xl text-lg w-full transition-all placeholder:text-gray-500"
                  data-testid="tone-input"
                />
              </div>

              <div>
                <Label htmlFor="constraints" className="text-lg font-semibold text-black mb-2 block">
                  Any specific requirements or constraints?
                </Label>
                <Textarea
                  id="constraints"
                  value={formData.constraints}
                  onChange={(e) => setFormData({ ...formData, constraints: e.target.value })}
                  placeholder="Example: Keep it under 500 words, include statistics"
                  className="min-h-[100px] bg-gray-50 border-2 border-transparent focus:border-[#0044CC] focus:bg-white rounded-xl text-lg p-4 w-full transition-all placeholder:text-gray-500 resize-none"
                  data-testid="constraints-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-14 px-12 rounded-full font-semibold text-lg transition-colors w-full md:w-auto"
                data-testid="generate-prompt-button"
              >
                {loading ? 'Generating...' : 'Generate Improved Prompt'}
              </Button>
            </form>

            {result && (
              <div className="mt-12 space-y-8" data-testid="result-section">
                <div className="bg-white border-2 border-gray-100 p-8 rounded-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl md:text-3xl font-semibold text-black">
                      Your Improved Prompt
                    </h2>
                    <Button
                      onClick={handleCopy}
                      className="bg-transparent border-2 border-[#0044CC] text-[#0044CC] hover:bg-[#0044CC]/5 h-12 px-6 rounded-full font-semibold transition-colors inline-flex items-center gap-2"
                      data-testid="copy-prompt-button"
                    >
                      {copied ? (
                        <>
                          <Check className="h-5 w-5" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-5 w-5" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <p className="text-base md:text-lg leading-relaxed text-black whitespace-pre-wrap" data-testid="improved-prompt-text">
                      {result.improved_prompt}
                    </p>
                  </div>
                </div>

                {result.suggested_tools && result.suggested_tools.length > 0 && (
                  <div className="bg-white border-2 border-gray-100 p-8 rounded-2xl">
                    <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">
                      Suggested Tools
                    </h2>
                    <p className="text-base md:text-lg text-[#333333] mb-6">
                      {result.explanation}
                    </p>
                    <div className="space-y-4">
                      {result.suggested_tools.map((tool, index) => (
                        <div
                          key={tool.id}
                          className="p-6 bg-gray-50 rounded-xl border-2 border-transparent hover:border-[#0044CC]/50 transition-colors"
                          data-testid={`suggested-tool-${index}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-grow">
                              <h3 className="text-xl font-semibold text-black mb-2">{tool.name}</h3>
                              <p className="text-base text-[#333333] mb-3">{tool.description_plain}</p>
                              <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-white text-black text-sm font-medium rounded-full">
                                  {tool.pricing_model}
                                </span>
                                <span className="px-3 py-1 bg-white text-black text-sm font-medium rounded-full">
                                  {tool.skill_level}
                                </span>
                              </div>
                            </div>
                            <Button
                              onClick={() => navigate(`/tool/${tool.id}`)}
                              className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-6 rounded-full font-semibold transition-colors flex-shrink-0"
                              data-testid={`view-tool-button-${index}`}
                            >
                              View Tool
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PromptHelperPage;